import type {
  IChartApi,
  IPrimitivePaneRenderer,
  ISeriesApi,
  ISeriesPrimitive,
  SeriesAttachedParameter,
  SeriesType,
  Time,
  UTCTimestamp,
} from 'lightweight-charts';
import { barAt } from '../drawings/measure';
import type { DealLike } from './tradeMarkers';

/** A trade's entry joined to one of its exits: two points on the chart and whether it made money. */
export interface TradeLink {
  id: string;
  t1: UTCTimestamp;
  p1: number;
  t2: UTCTimestamp;
  p2: number;
  won: boolean;
}

/**
 * Pairs each exit with the entry of the same position (a position closed in parts gives one link per part), on this symbol,
 * for deals whose bars are loaded. Same time rules as the arrows: deal times are UTC, bar times are server-clock seconds.
 */
export function tradeLinks(
  deals: readonly DealLike[],
  symbol: string,
  bars: readonly { t: number }[],
  offsetMinutes: number,
): TradeLink[] {
  const entries = new Map<number, DealLike>();
  for (const d of deals) {
    if (d.symbol === symbol && d.entry === 'In' && d.positionId !== undefined && !entries.has(d.positionId)) {
      entries.set(d.positionId, d);
    }
  }
  const at = (d: DealLike) => barAt(bars, Math.floor(Date.parse(d.time) / 1000) + offsetMinutes * 60);
  const out: TradeLink[] = [];
  for (const d of deals) {
    if (d.symbol !== symbol || d.positionId === undefined || (d.entry !== 'Out' && d.entry !== 'InOut'))
      continue;
    const opened = entries.get(d.positionId);
    if (!opened) continue;
    const from = at(opened);
    const to = at(d);
    if (!from || !to) continue;
    out.push({
      id: `link-${d.ticket}`,
      t1: from.t as UTCTimestamp,
      p1: opened.price,
      t2: to.t as UTCTimestamp,
      p2: d.price,
      won: d.profit + d.commission + d.swap > 0,
    });
  }
  return out;
}

type Target = Parameters<IPrimitivePaneRenderer['draw']>[0];

/** Draws the links as dashed lines under the arrows, green for a winning trade and red for a losing one. */
export class TradeLinksPrimitive implements ISeriesPrimitive<Time> {
  private chart: IChartApi | null = null;
  private series: ISeriesApi<SeriesType> | null = null;
  private redraw: (() => void) | null = null;
  private links: TradeLink[] = [];
  private colors = { up: '#26a69a', down: '#ef5350' };

  attached(param: SeriesAttachedParameter<Time>) {
    this.chart = param.chart as IChartApi;
    this.series = param.series as ISeriesApi<SeriesType>;
    this.redraw = param.requestUpdate;
  }

  detached() {
    this.chart = null;
    this.series = null;
    this.redraw = null;
  }

  set(links: TradeLink[], colors: { up: string; down: string }) {
    this.links = links;
    this.colors = colors;
    this.redraw?.();
  }

  paneViews() {
    return [
      {
        zOrder: () => 'normal' as const,
        renderer: (): IPrimitivePaneRenderer => ({
          draw: (target: Target) => {
            const chart = this.chart;
            const series = this.series;
            if (!chart || !series) return;
            target.useMediaCoordinateSpace(({ context }) => {
              context.save();
              context.lineWidth = 1.5;
              context.setLineDash([5, 4]);
              for (const l of this.links) {
                const x1 = chart.timeScale().timeToCoordinate(l.t1);
                const x2 = chart.timeScale().timeToCoordinate(l.t2);
                const y1 = series.priceToCoordinate(l.p1);
                const y2 = series.priceToCoordinate(l.p2);
                if (x1 === null || x2 === null || y1 === null || y2 === null) continue;
                context.strokeStyle = l.won ? this.colors.up : this.colors.down;
                context.beginPath();
                context.moveTo(x1, y1);
                context.lineTo(x2, y2);
                context.stroke();
              }
              context.restore();
            });
          },
        }),
      },
    ];
  }
}
